import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../environments/environment";
import { Observable, from, of } from "rxjs";
import { catchError } from "rxjs/operators";

declare global {
	interface Window {
		grecaptcha: any;
		onRecaptchaLoaded: () => void;
	}
}

/**
 * How long to wait for Google's script to load, and for it to produce a token.
 * Both normally take 1-2 seconds.
 */
const RECAPTCHA_TIMEOUT_MS = 5000;

export type RecaptchaFailureReason = "load_failed" | "timeout" | "unavailable" | "execute_failed";

/**
 * reCAPTCHA couldn't run in this browser, typically because a network filter,
 * extension or tracking protection blocks Google's script.
 */
export class RecaptchaError extends Error {
	constructor(
		public readonly reason: RecaptchaFailureReason,
		public readonly detail?: unknown,
	) {
		super(`reCAPTCHA ${reason}`);
		this.name = "RecaptchaError";
	}
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const timer = setTimeout(() => reject(new RecaptchaError("timeout")), ms);
		promise.then(
			(value) => {
				clearTimeout(timer);
				resolve(value);
			},
			(error) => {
				clearTimeout(timer);
				reject(error);
			},
		);
	});
}

@Injectable({
	providedIn: "root",
})
export class RecaptchaService {
	private isBrowser: boolean;
	/** The script request itself; shared by every caller until it loads or errors. */
	private scriptLoad: Promise<void> | null = null;

	constructor(@Inject(PLATFORM_ID) platformId: any) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	/**
	 * Load the reCAPTCHA script
	 */
	public loadRecaptchaScript(): Promise<void> {
		if (!this.isBrowser) {
			return Promise.resolve();
		}

		if (!this.scriptLoad) {
			this.scriptLoad = new Promise<void>((resolve, reject) => {
				if (typeof window.grecaptcha !== "undefined") {
					resolve();
					return;
				}

				const script = document.createElement("script");
				// Use Enterprise API as per Google documentation
				script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptcha.siteKey}`;
				script.async = true;
				script.defer = true;
				script.onload = () => resolve();
				script.onerror = (error) => {
					// Drop the failed tag so the next attempt requests the script afresh
					script.remove();
					this.scriptLoad = null;
					reject(new RecaptchaError("load_failed", error));
				};

				document.head.appendChild(script);
			});
		}

		// A blocked request can hang rather than error, so each caller stops waiting after
		// the timeout. The request itself carries on: if the script arrives late, the next
		// attempt uses it straight away instead of requesting it again.
		return withTimeout(this.scriptLoad, RECAPTCHA_TIMEOUT_MS);
	}

	/**
	 * Execute reCAPTCHA Enterprise
	 * @param action The action to execute
	 * @returns Promise with the token; rejects with a RecaptchaError if reCAPTCHA can't run
	 */
	public async executeRecaptcha(action: string): Promise<string> {
		if (!this.isBrowser) {
			return "";
		}

		try {
			await this.loadRecaptchaScript();
		} catch (error) {
			throw error instanceof RecaptchaError ? error : new RecaptchaError("load_failed", error);
		}

		// Right after the script loads only `ready` exists; `execute` is added once
		// reCAPTCHA has initialised, so look it up inside the ready callback.
		if (!window.grecaptcha?.enterprise?.ready) {
			throw new RecaptchaError("unavailable");
		}

		let token: string;
		try {
			token = await withTimeout(
				new Promise<void>((resolve) => window.grecaptcha.enterprise.ready(resolve)).then(() => {
					if (!window.grecaptcha.enterprise.execute) {
						throw new RecaptchaError("unavailable");
					}
					return window.grecaptcha.enterprise.execute(environment.recaptcha.siteKey, { action });
				}),
				RECAPTCHA_TIMEOUT_MS,
			);
		} catch (error) {
			throw error instanceof RecaptchaError ? error : new RecaptchaError("execute_failed", error);
		}

		if (!token) {
			throw new RecaptchaError("execute_failed");
		}
		return token;
	}

	/**
	 * Get reCAPTCHA Enterprise token
	 * @param action The action to execute
	 * @returns Promise with the token
	 */
	public getToken(action: string): Promise<string> {
		return this.executeRecaptcha(action);
	}

	/**
	 * Render a reCAPTCHA v2 widget to the specified container (Enterprise mode returns -1)
	 * @param container The DOM element or ID where the reCAPTCHA should be rendered
	 * @param options Optional reCAPTCHA options
	 * @returns The ID of the reCAPTCHA widget
	 */
	public renderRecaptcha(
		container: string | HTMLElement,
		options: any = {}
	): Observable<number> {
		// Enterprise reCAPTCHA doesn't need visual widget
		return of(-1);
	}

	/**
	 * Check if the reCAPTCHA has been verified (Enterprise mode returns empty string)
	 * @param widgetId The ID of the reCAPTCHA widget to check
	 * @returns True if the reCAPTCHA has been verified
	 */
	public getResponse(widgetId: number): string {
		// Enterprise reCAPTCHA doesn't use widget responses
		return "";
	}

	/**
	 * Reset the reCAPTCHA widget
	 * @param widgetId The ID of the reCAPTCHA widget to reset
	 */
	public resetRecaptcha(widgetId: number): void {
		if (!this.isBrowser || widgetId === -1) {
			return;
		}

		try {
			window.grecaptcha.reset(widgetId);
		} catch (error) {
			// Silently handle reset errors
		}
	}
}
